import api from './api.config';
import { toast } from 'react-toastify';

/**
 * File Upload Service
 * Handles file uploads to S3 via backend API
 */

// Allowed file types and sizes
const FILE_CONFIG = {
  documents: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    allowedExtensions: ['pdf', 'doc', 'docx']
  },
  resumes: {
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    allowedExtensions: ['pdf', 'doc', 'docx']
  },
  images: {
    maxSize: 2 * 1024 * 1024, // 2MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif']
  }
};

class FileUploadService {
  /**
   * Validate file before upload
   */
  validateFile(file, type = 'documents') {
    const config = FILE_CONFIG[type] || FILE_CONFIG.documents;
    
    // Check file size
    if (file.size > config.maxSize) {
      const maxSizeMB = config.maxSize / (1024 * 1024);
      throw new Error(`File size must be less than ${maxSizeMB}MB`);
    }
    
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!config.allowedExtensions.includes(fileExtension)) {
      throw new Error(`File type must be one of: ${config.allowedExtensions.join(', ')}`);
    }
    
    // Additional MIME type check
    if (file.type && !config.allowedTypes.includes(file.type)) {
      console.warn('MIME type mismatch:', file.type);
    }
    
    return true;
  }

  /**
   * Upload file to S3 via backend
   */
  async uploadFile(file, options = {}) {
    try {
      // Validate file
      this.validateFile(file, options.type || 'documents');
      
      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Add additional fields if provided
      if (options.panelId) formData.append('panelId', options.panelId);
      if (options.userId) formData.append('userId', options.userId);
      if (options.documentType) formData.append('documentType', options.documentType);
      if (options.description) formData.append('description', options.description);
      
      // Show upload progress
      const progressCallback = options.onProgress || ((percent) => {
        console.log(`Upload progress: ${percent}%`);
      });
      
      // Make API call
      const response = await api.post('/api/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        }
      });
      
      if (response.data.success) {
        toast.success('File uploaded successfully');
        return {
          success: true,
          document: response.data.document,
          url: response.data.document.url
        };
      }
      
      throw new Error(response.data.message || 'Upload failed');
      
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.message || 'Failed to upload file');
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload multiple files
   */
  async uploadMultipleFiles(files, options = {}) {
    const results = [];
    const errors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await this.uploadFile(file, {
          ...options,
          onProgress: (percent) => {
            if (options.onProgress) {
              // Calculate overall progress
              const overallProgress = ((i * 100) + percent) / files.length;
              options.onProgress(Math.round(overallProgress));
            }
          }
        });
        
        if (result.success) {
          results.push(result.document);
        } else {
          errors.push({ file: file.name, error: result.error });
        }
      } catch (error) {
        errors.push({ file: file.name, error: error.message });
      }
    }
    
    if (errors.length > 0) {
      console.error('Some files failed to upload:', errors);
      toast.warning(`${results.length} files uploaded, ${errors.length} failed`);
    } else if (results.length > 0) {
      toast.success(`All ${results.length} files uploaded successfully`);
    }
    
    return {
      success: errors.length === 0,
      uploaded: results,
      errors
    };
  }

  /**
   * Upload resume for cadre application
   */
  async uploadResume(file, applicationId) {
    try {
      this.validateFile(file, 'resumes');
      
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('applicationId', applicationId);
      
      const response = await api.post('/api/cadre/upload-resume', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success('Resume uploaded successfully');
        return {
          success: true,
          resumeUrl: response.data.resumeUrl
        };
      }
      
      throw new Error(response.data.message || 'Resume upload failed');
      
    } catch (error) {
      console.error('Resume upload error:', error);
      toast.error(error.message || 'Failed to upload resume');
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Download file from S3
   */
  async downloadFile(documentId, fileName) {
    try {
      const response = await api.get(`/api/documents/download/${documentId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'document');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
      return { success: true };
      
    } catch (error) {
      console.error('File download error:', error);
      toast.error('Failed to download file');
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete file from S3
   */
  async deleteFile(documentId) {
    try {
      const response = await api.delete(`/api/documents/${documentId}`);
      
      if (response.data.success) {
        toast.success('File deleted successfully');
        return { success: true };
      }
      
      throw new Error(response.data.message || 'Delete failed');
      
    } catch (error) {
      console.error('File delete error:', error);
      toast.error('Failed to delete file');
      return { success: false, error: error.message };
    }
  }

  /**
   * Get signed URL for direct S3 upload (for large files)
   */
  async getSignedUploadUrl(fileName, fileType) {
    try {
      const response = await api.post('/api/documents/signed-url', {
        fileName,
        fileType
      });
      
      if (response.data.success) {
        return {
          success: true,
          uploadUrl: response.data.uploadUrl,
          fileUrl: response.data.fileUrl
        };
      }
      
      throw new Error(response.data.message || 'Failed to get upload URL');
      
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Direct upload to S3 using signed URL
   */
  async uploadDirectToS3(file, uploadUrl, onProgress) {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });
      
      if (response.ok) {
        toast.success('File uploaded successfully');
        return { success: true };
      }
      
      throw new Error('Direct upload failed');
      
    } catch (error) {
      console.error('Direct S3 upload error:', error);
      toast.error('Failed to upload file directly');
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload large file in chunks
   */
  async uploadLargeFile(file, options = {}) {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const chunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadedParts = [];
    
    try {
      // Initialize multipart upload
      const initResponse = await api.post('/api/documents/multipart/init', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });
      
      if (!initResponse.data.success) {
        throw new Error('Failed to initialize multipart upload');
      }
      
      const uploadId = initResponse.data.uploadId;
      
      // Upload each chunk
      for (let i = 0; i < chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('uploadId', uploadId);
        formData.append('partNumber', i + 1);
        
        const partResponse = await api.post('/api/documents/multipart/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (options.onProgress) {
              const overallProgress = ((i * CHUNK_SIZE + progressEvent.loaded) / file.size) * 100;
              options.onProgress(Math.round(overallProgress));
            }
          }
        });
        
        uploadedParts.push({
          partNumber: i + 1,
          etag: partResponse.data.etag
        });
      }
      
      // Complete multipart upload
      const completeResponse = await api.post('/api/documents/multipart/complete', {
        uploadId,
        parts: uploadedParts,
        fileName: file.name
      });
      
      if (completeResponse.data.success) {
        toast.success('Large file uploaded successfully');
        return {
          success: true,
          document: completeResponse.data.document
        };
      }
      
      throw new Error('Failed to complete multipart upload');
      
    } catch (error) {
      console.error('Large file upload error:', error);
      toast.error('Failed to upload large file');
      return { success: false, error: error.message };
    }
  }

  /**
   * Get file preview URL
   */
  getPreviewUrl(document) {
    if (document.url) {
      return document.url;
    }
    if (document.s3Key) {
      return `/api/documents/preview/${document.id}`;
    }
    return null;
  }
}

// Create singleton instance
const fileUploadService = new FileUploadService();

// Export service
export default fileUploadService;

// Export for named imports
export {
  fileUploadService,
  FILE_CONFIG
};
